/**
 * Custom schema for rack rail underlay panel 2D preview
 * Reuses the interiorSchema rendering for panels
 * Shows a single unified view of all underlay panels
 */

import { Sides } from "@project/emerge-types";
import {
  calculateInteriorRects,
  type DrawContext,
  drawRects,
  type InteriorLayoutData,
  type PanelData,
} from "./interiorSchema";

/**
 * Underlay panel entry for 2D preview (matches PanelData structure)
 */
export interface UnderlayPreviewEntry {
  position: { x: number; y: number; z: number };
  size: { x: number; y: number }; // width, height of the panel
  side: number; // Sides enum value (LEFT or RIGHT)
  strength: number; // thickness
  orientation?: number; // Rotation in degrees within the panel's plane
  color?: { r: number; g: number; b: number }; // RGB color from panel material (0-1 range)
}

/**
 * Convert UnderlayPreviewEntry to PanelData for interiorSchema
 */
function convertToPanelData(entry: UnderlayPreviewEntry): PanelData {
  return {
    position: entry.position,
    size: entry.size,
    side: entry.side,
    strength: entry.strength,
    orientation: entry.orientation,
    color: entry.color,
  };
}

/**
 * Draw underlay panels using the interiorSchema rendering
 * Shows a single unified view (not grouped by side)
 */
async function drawUnderlayPreviews(arg: {
  pdfDoc: import("pdf-lib").PDFDocument;
  page: import("pdf-lib").PDFPage;
  schema: {
    position: { x: number; y: number };
    width: number;
    height: number;
  };
  value: string; // JSON array of UnderlayPreviewEntry
}): Promise<void> {
  const { pdfDoc, page, schema, value } = arg;
  const { rgb, StandardFonts } = await import("pdf-lib");

  if (!value || value.trim().length === 0 || value === "[]") {
    return;
  }

  let underlays: UnderlayPreviewEntry[];
  try {
    underlays = JSON.parse(value);
    if (!Array.isArray(underlays) || underlays.length === 0) {
      return;
    }
  } catch {
    return;
  }

  const { x, y } = schema.position;
  const { width, height } = schema;

  // mm to points conversion (1mm = 2.83465 points)
  const MM_TO_PT = 2.83465;
  const padding = 4; // points

  // PDF coordinates: origin at bottom-left, y increases upward
  const pageHeight = page.getHeight();
  const baseX = x * MM_TO_PT;
  const baseY = pageHeight - y * MM_TO_PT;

  // Convert all underlays to PanelData
  const panels: PanelData[] = underlays.map(convertToPanelData);

  // Determine view direction based on first underlay's side
  // LEFT/RIGHT sides → view from that side (left or right)
  const firstSide = underlays[0]?.side ?? Sides.LEFT;
  const viewDirection = firstSide === Sides.LEFT ? Sides.LEFT : Sides.RIGHT;

  // Create interior layout data for all panels
  const layoutData: InteriorLayoutData = {
    panels,
    viewDirection,
  };

  // Calculate rectangles using interiorSchema
  const { rects, totalWidth, totalHeight } = calculateInteriorRects(layoutData);

  if (rects.length === 0) {
    return;
  }

  // Sort by zLayer (back to front)
  const sortedRects = [...rects].sort((a, b) => {
    const zA = a.zLayer ?? 0;
    const zB = b.zLayer ?? 0;
    return zA - zB;
  });

  // Calculate zLayer range for depth visualization
  const zLayers = sortedRects
    .map((r) => r.zLayer)
    .filter((z): z is number => z !== undefined);
  const minZ = zLayers.length > 0 ? Math.min(...zLayers) : 0;
  const maxZ = zLayers.length > 0 ? Math.max(...zLayers) : 0;
  const zRange = Math.max(1, maxZ - minZ);

  // Calculate scale to fit in available area
  const availableWidth = width * MM_TO_PT - padding * 2;
  const availableHeight = height * MM_TO_PT - padding * 2;
  const scaleX = availableWidth / totalWidth;
  const scaleY = availableHeight / totalHeight;
  const scale = Math.min(scaleX, scaleY) * 0.9; // 90% to leave margin

  // Center offset
  const offsetX = (availableWidth - totalWidth * scale) / 2;
  const offsetY = (availableHeight - totalHeight * scale) / 2;

  // Embed font for labels (if any)
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

  // Create draw context
  const ctx: DrawContext = {
    page,
    baseX: baseX + padding,
    baseY: baseY - height * MM_TO_PT + padding,
    offsetX,
    offsetY,
    scale,
    totalHeight,
    minZ,
    zRange,
    font,
    rgb,
  };

  // Draw using the shared drawRects function
  await drawRects(sortedRects, ctx);
}

export const rackRailPreviewSchema = {
  ui: (arg: { rootElement: HTMLElement; value: string }) => {
    const div = document.createElement("div");
    div.style.width = "100%";
    div.style.height = "100%";
    div.style.backgroundColor = "transparent";
    div.style.display = "flex";
    div.style.alignItems = "center";
    div.style.justifyContent = "center";
    div.style.padding = "4px";
    div.style.fontSize = "10px";
    div.style.color = "#666";

    try {
      const underlays: UnderlayPreviewEntry[] = JSON.parse(arg.value || "[]");

      if (underlays.length === 0) {
        div.textContent = "Keine Unterlegplatten";
        arg.rootElement.appendChild(div);
        return;
      }

      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("width", "100%");
      svg.setAttribute("height", "100%");
      svg.setAttribute("viewBox", "0 0 100 100");
      svg.style.border = "1px solid #ccc";
      svg.style.backgroundColor = "#fafafa";

      // Draw simplified rectangles for preview (all panels)
      let yOffset = 10;
      const rectHeight = Math.min(20, 80 / underlays.length);
      for (const _underlay of underlays) {
        const rect = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "rect",
        );
        rect.setAttribute("x", "15");
        rect.setAttribute("y", String(yOffset));
        rect.setAttribute("width", "70");
        rect.setAttribute("height", String(rectHeight));
        rect.setAttribute("fill", "#4d4d4d");
        rect.setAttribute("stroke", "#333");
        rect.setAttribute("stroke-width", "1");
        svg.appendChild(rect);
        yOffset += rectHeight + 5;
      }

      div.appendChild(svg);
    } catch {
      div.textContent = "Invalid underlay data";
    }

    arg.rootElement.appendChild(div);
  },

  pdf: async (arg: {
    pdfDoc: import("pdf-lib").PDFDocument;
    page: import("pdf-lib").PDFPage;
    schema: {
      position: { x: number; y: number };
      width: number;
      height: number;
    };
    value: string;
  }) => {
    await drawUnderlayPreviews(arg);
  },

  propPanel: {
    schema: {},
    defaultSchema: {
      name: "rackRailPreview",
      type: "rackRailPreview",
      position: { x: 0, y: 0 },
      width: 95,
      height: 30,
    },
    defaultValue: "[]",
  },
};

export default rackRailPreviewSchema;
